import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';

// Style components
import styled from 'styled-components';
import { breakpoints } from 'styles/Breakpoints';
import { Grid } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';

// Assets
import pigeon from 'assets/images/pigeon.png';
import helpIcon from 'assets/images/willStepsHelpIcon.png';
import book from 'assets/svg/book.svg';
import contact from 'assets/svg/menuItem-contact.svg';

// Objects, Utils, Store, Components
import { Pages } from 'routes';
import { Mixpanel } from 'utils/mixpanel';
import Navbar from 'Pages/Home/Navbar';
import OverlayHead from 'Pages/MyWill/CreateWill/Steps/OverlayHead';
import BackToSectionSummary from 'Pages/MyWill/BackToSectionSummary';
import { AssetSubTypesWrapper, HelpWrapper, QueryHelpCard } from 'Pages/MyWill/NewMyWill';
import { CourierRequestForm } from 'Pages/MyWill/PostWillGeneration/PrintAndCourier/CourierRequestForm';
import PrintAndCourierRequestTracker from 'Pages/MyWill/PostWillGeneration/PrintAndCourier/RequestStatusTracker';
import ContactUsHome from 'Pages/ContactUs';
import { useVendor } from 'common/useVendor';
import Header from '../../../../common/Header';
import CustomButton from 'common/Reusables/Button';
import MuiDrawer from 'common/Reusables/Drawer';
import { selectActiveWill, selectCurrentUserPlans } from 'store/user/selectors';
import { useAppSelector } from 'store/hooks';
import { fetchCurrentUserPlans } from 'store/user/slice';
import { MainPlanModules } from 'store/payments/paymentsSaga';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 7%;
  padding-left: 33px;
  padding-right: 33px;
  @media screen and (max-width: ${breakpoints.md}) {
    padding-left: 24px;
    padding-right: 24px;
    box-sizing: border-box;
    overflow: hidden;
    width: 100%;
    max-width: 100%;
  }
  .title {
    font-weight: 700;
    font-size: 52px;
    @media screen and (max-width: ${breakpoints.md}) {
      font-size: 24px;
    }
  }

  .pigeon {
    width: 294px;
    height: 258px;
    margin-top: 36px;
    margin-bottom: 48px;
    @media screen and (max-width: ${breakpoints.md}) {
      width: 136px;
      height: 119px;
      margin-top: 48px;
      margin-bottom: 48px;
    }
  }

  .info-text {
    margin-top: 50px;
    margin-bottom: 56px;
    font-weight: 700;
    font-size: 28px;
    @media screen and (max-width: ${breakpoints.md}) {
      margin: 0px;
      text-align: center;
      margin-bottom: 32px;
      font-size: 20px;
    }
  }
`;

export default function PrintAndCourierFlow({ resetToMyWill }: { resetToMyWill: Function }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const { isAcko } = useVendor();
  const themew = useTheme();
  const lessThanSmall = useMediaQuery(themew.breakpoints.down('sm'));
  const userPlans = useAppSelector(selectCurrentUserPlans);
  const userActiveWill = useAppSelector(selectActiveWill);
  const [nextPage, setNextPage] = useState<boolean>(location.state?.fromPayment || false);
  const [showInfoScreen, setShowInfoScreen] = useState<boolean>(false);
  const [needsPayment, setNeedsPayment] = useState<boolean>(true);
  const [showStepper, setShowStepper] = useState(false);
  const [showContactUsScreen, setShowContactUsScreen] = useState(false);


  useEffect(() => {
    dispatch(fetchCurrentUserPlans());
  }, [])

  // [REPLACED] Check if shipping and courier module is enabled to determine if user needs to make payment to submit request
  // useEffect(() => {
  //   if (!userPlans.data?.fetchPlans) return;
  //   if (userPlans.data?.fetchPlans.length === 0) return;

  //   const activePlan = userPlans.data.fetchPlans.filter(pl => pl.isActive)[0];
  //   if (Date.now() > Number(activePlan.expireAt)) return;

  //   const shippingAndCourier = activePlan?.modules ? activePlan.modules[MainPlanModules.SHIPPING_AND_COURIER] : null;

  //   if (shippingAndCourier && shippingAndCourier.enabled && shippingAndCourier.useCount > 0) {
  //     console.log('shipping', shippingAndCourier)
  //     console.log('shipping 1', shippingAndCourier.enabled)
  //     console.log('shipping 2', shippingAndCourier.useCount)
  //     setNeedsPayment(false);
  //   } else {
  //     setNeedsPayment(true);
  //   }

  // }, [userPlans])


  useEffect(() => {
    const plans = userPlans.data?.fetchPlans || [];
    if (!plans.length) return;

    // pick an active, non-expired plan
    const now = Date.now();
    const activePlan = plans.find(pl => pl.isActive && Number(pl.expireAt) > now);
    if (!activePlan) { setNeedsPayment(true); return; }

    // normalize plan id/name
    const planId: string =
      (activePlan.planId ?? activePlan.planId ?? activePlan.planId ?? '').toString().toLowerCase();

    // shipping module details
    const shippingMod = activePlan?.modules?.[MainPlanModules.SHIPPING_AND_COURIER];

    // business rules:
    // starter => always pay
    // pro     => free if module exists AND enabled AND useCount > 0, else pay
    if (planId.includes('starter')) {
      setNeedsPayment(true);
      return;
    }

    if (planId.includes('pro')) {
      const hasCredit = Boolean(shippingMod && shippingMod.enabled && (shippingMod.useCount ?? 0) > 0);
      setNeedsPayment(!hasCredit);
      return;
    }

    // any other plan tiers default to: pay unless a credit is present
    const hasCredit = Boolean(shippingMod && shippingMod.enabled && (shippingMod.useCount ?? 0) > 0);
    setNeedsPayment(!hasCredit);
  }, [userPlans]);


  useEffect(() => {
    if (
      userActiveWill &&
      userActiveWill.courierRequestInfo &&
      userActiveWill.courierRequestInfo !== null
    ) {
      setShowStepper(true);
    }
  }, [userActiveWill]);

  const onHelpClickRightSide = () => {
    setShowInfoScreen(true);
    Mixpanel.track('Question mark button clicked', {
      card_name: 'Print & Courier Will',
    });
  };

  // If user has already submitted a courier request, show the tracker
  if (showStepper) return <PrintAndCourierRequestTracker />

  return (
    <>
      <div className={'hide_in_mobile'}>
        <Navbar backgroundColorClass='background_white' />
      </div>

      {/* nextPage -> If user is redirected here from paymentFlow then skip "get started" page and go straight to the form */}
      {nextPage ? (
        <CourierRequestForm
          resetToMyWill={resetToMyWill}
          setShowStepper={setShowStepper}
        />
      ) : (
        // Show "get started" page if user NOT from paymentFlow
        <>
          <div
            style={{
              position: 'relative',
              top: 0,
              display: 'flex',
              justifyContent: 'space-between',
              ...(lessThanSmall
                ? {
                  alignItems: 'center',
                  paddingLeft: '24px',
                  paddingRight: '30px',
                  paddingTop: '50px',
                }
                : {
                  alignItems: 'center',
                  paddingLeft: '80px',
                  paddingRight: '80px',
                  paddingTop: '33px',
                }),
            }}
          >
            <div className={'hide_in_mobile'}>
              <BackToSectionSummary
                noMargin={true}
                text='Back to My Will'
                action={resetToMyWill}
              />
            </div>
            <div className={'show_in_mobile w-100'}>
              <Header
                backgroundColorClass={'background_white'}
                // @ts-ignore
                centerItems={true}
                backButton={true}
                title={'My Will'}
                backButtonOnClick={resetToMyWill}
                hideAvatar={true}
                renderRightHandSide={
                  lessThanSmall ? (
                    <HelpOutlineIcon onClick={onHelpClickRightSide} />
                  ) : (
                    <img
                      style={{
                        height: '60px',
                        ...(lessThanSmall ? {} : { marginTop: '33px', marginRight: '80px' }),

                        cursor: 'pointer',
                      }}
                      src={helpIcon}
                      alt='Help Icon'
                      onClick={onHelpClickRightSide}
                    />
                  )
                }
              />
            </div>
            <div className={'hide_in_mobile'}>
              {lessThanSmall ? (
                <HelpOutlineIcon onClick={onHelpClickRightSide} />
              ) : (
                <img
                  style={{
                    height: '60px',
                    ...(lessThanSmall ? {} : { marginTop: '33px', marginRight: '80px' }),

                    cursor: 'pointer',
                  }}
                  src={helpIcon}
                  alt='Help Icon'
                  onClick={onHelpClickRightSide}
                />
              )}
            </div>
          </div>
          <MuiDrawer
            open={showInfoScreen}
            hideDrawer={() => setShowInfoScreen(false)}
            width={'min(100%, 500px)'}
          >
            <AssetSubTypesWrapper>
              <OverlayHead
                heading='Print & Courier Will'
                onClick={() => setShowInfoScreen(false)}
                setShowHelp={() => setShowInfoScreen(true)}
              />
              <p className='notify-help-text'>
                According to the current law in India, a Will is not valid unless it has been
                physically signed in the presence of 2 witnesses.
              </p>
              <p className='notify-help-text'>
                If you would like, we are happy to print and ship your Will to you free of charge*.
                You can also get a soft copy emailed to you and print it out yourself.
              </p>
              <p className='notify-help-text' style={{ color: '#666B75' }}>
                * Free for first-time Will requests only. Updates may be printed and shipped for a
                fee.
              </p>
            </AssetSubTypesWrapper>
            <HelpWrapper container>
              {!isAcko && (
                <Grid
                  item
                  lg={12}
                  className='queries_section'
                  display={'flex'}
                  flexDirection='column'
                  justifyContent='space-around'
                  alignItems={'center'}
                >
                  <p className='subText font-semi-bold'>Have a specific query in mind?</p>
                  <QueryHelpCard
                    icon={book}
                    text='Explore FAQs'
                    onClick={() => window.open('https://getyellow.in/faq', '_blank')}
                  />
                  <QueryHelpCard
                    icon={contact}
                    text='Contact Us'
                    onClick={
                      () => {
                        setShowContactUsScreen(true);
                      }}
                  />
                </Grid>
              )}
            </HelpWrapper>
          </MuiDrawer>

          <MuiDrawer
            open={showContactUsScreen}
            width={'min(100%, 500px)'}
            hideDrawer={() => setShowContactUsScreen(false)}
          // anchor={lessThanSmall ? 'bottom' : 'right'}
          // borderRadius={lessThanSmall ? '24px 24px 0px 0px' : ''}
          >
            <ContactUsHome closeIconAction={() => setShowContactUsScreen(false)} />
          </MuiDrawer>

          <Wrapper>
            <p className='title'>Print & Courier Will</p>
            <img src={pigeon} alt='' className='pigeon' />
            <p className='info-text'>Get your Will shipped to you</p>
            {/* <div className={'show_in_mobile'}> */}
            {/*   <div */}
            {/*     className={'flex flex-row p-16px items-center justify-center gap-16px'} */}
            {/*     style={{ */}
            {/*       background: '#FFF8DD', */}
            {/*       borderRadius: 12, */}
            {/*       border: '0.50px #FFC632 solid', */}
            {/*     }} */}
            {/*   > */}
            {/*     <div> */}
            {/*       <img className={'h-24px w-24px'} src={print_courier_first_time_info_icon} /> */}
            {/*     </div> */}
            {/*     <div> */}
            {/*       <span */}
            {/*         className={'font-roboto'} */}
            {/*         style={{ color: '#675E40', fontSize: '14px', verticalAlign: 'middle' }} */}
            {/*       > */}
            {/*         This service is complimentary for first-time requests */}
            {/*       </span> */}
            {/*     </div> */}
            {/*   </div> */}
            {/* </div> */}
            <CustomButton
              text='Get Started'
              style={
                lessThanSmall
                  ? { position: 'absolute', bottom: 0, width: '100%', borderRadius: '0px' }
                  : { width: '368px', borderRadius: '12px' }
              }
              onClick={() => {
                if (needsPayment) {
                  navigate(Pages.PAYMENT + "?selectedPlans=shippingAndCourier", { state: { fromCourier: true } });
                } else {
                  setNextPage(true)
                }
              }}
            />
          </Wrapper>
        </>
      )}
    </>
  );
}
